/**
 * Parses a team path string to extract team name, project name, and sprint identifier.
 * 
 * Format: "TeamName:ProjectName\\Path\\Sprint Identifier"
 * Example: "Atlas:Platform\\2025\\Q4\\Sprint 20251204" -> { teamName: "Atlas", projectName: "Platform", sprintName: "20251204" }
 * 
 * @param teamPath - The team path string from story_points_per_team
 * @returns Object containing parsed team name, project name, and sprint identifier
 */
export const parseTeamPath = (teamPath: string): {
  teamName: string;
  projectName: string;
  sprintName: string;
} => {
  if (!teamPath) {
    return {
      teamName: '',
      projectName: '',
      sprintName: ''
    };
  }

  const colonIndex = teamPath.indexOf(':');
  if (colonIndex === -1) {
    // No colon found, treat entire string as team name
    return {
      teamName: teamPath.trim(),
      projectName: '',
      sprintName: ''
    };
  }

  // Extract team name (before colon)
  const teamName = teamPath.substring(0, colonIndex).trim();
  const rest = teamPath.substring(colonIndex + 1).trim();
  
  if (!rest) {
    return {
      teamName,
      projectName: '',
      sprintName: ''
    };
  }
  
  // Split by backslashes
  const parts = rest.split('\\').map(p => p.trim()).filter(Boolean);
  
  if (parts.length === 0) {
    return {
      teamName,
      projectName: '',
      sprintName: ''
    };
  }

  // First part after colon is the project name (before any backslashes)
  const projectName = parts[0];

  // Find the last part that contains "Sprint" (case insensitive)
  // Only extract the sprint identifier, not the word "Sprint"
  let sprintName = '';
  for (let i = parts.length - 1; i >= 0; i--) {
    if (parts[i].toLowerCase().includes('sprint')) {
      // Extract just the identifier (e.g., "20251204" from "Sprint 20251204")
      const sprintMatch = parts[i].match(/sprint\s*(.+)/i);
      if (sprintMatch && sprintMatch[1]) {
        sprintName = sprintMatch[1].trim();
      } else {
        // If no identifier found after "Sprint", try to extract any numbers/identifiers
        const identifierMatch = parts[i].match(/(\d+|\w+)$/i);
        if (identifierMatch) {
          sprintName = identifierMatch[1].trim();
        }
      }
      break;
    }
  }
  
  return {
    teamName,
    projectName: projectName || '',
    sprintName: sprintName || ''
  };
};